import { FormEvent, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SitesMultiCombobox } from '@/components/common/sites-multi-combobox';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { embryoStatusOptions } from '@/pages/embryo-app/components/EmbryoForm/form-options';
import type { StorageLocation } from '@/rtk-query.gql';
import type { ShipmentEmbryo, ShipmentSemen } from '@/services/shipping/types';
import type { SiteOption } from '@/services/sites';

export type BulkEditShipmentItemType = 'embryo' | 'semen';

type StatusOption = { label: string; value: number | string };

const statusOptions: StatusOption[] = embryoStatusOptions;

export type BulkEditShipmentItemsUpdates = {
  storageLocationId?: string;
  storageLocationName?: string | null;
  siteId?: string;
  status?: number;
};

type BulkEditShipmentItemsDialogProps = {
  visible: boolean;
  itemType: BulkEditShipmentItemType;
  selectedItems: ShipmentEmbryo[] | ShipmentSemen[];
  onHide: () => void;
  onSubmit: (updates: BulkEditShipmentItemsUpdates) => Promise<void>;
  isSubmitting?: boolean;
  allowStatusUpdate?: boolean;
  allowStorageLocationUpdate?: boolean;
};

export function BulkEditShipmentItemsDialog({
  visible,
  itemType,
  selectedItems,
  onHide,
  onSubmit,
  isSubmitting = false,
  allowStatusUpdate = false,
  allowStorageLocationUpdate = true,
}: BulkEditShipmentItemsDialogProps) {
  const { t } = useTranslation();
  const bulkEmbryoSiteEnabled = useFeatureFlag('Shipping_Bulk_Embryo_Site');
  const singleEmbryoEditEnabled = useFeatureFlag('Shipping_Single_Embryo_Edit');
  const [storageLocation, setStorageLocation] =
    useState<StorageLocation | null>(null);
  const [selectedSites, setSelectedSites] = useState<SiteOption[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<StatusOption | null>(
    null,
  );
  const [statusSearch, setStatusSearch] = useState('');

  const selectedSiteIds = useMemo(
    () => selectedSites.map((s) => s.id),
    [selectedSites],
  );

  const handleStatusChange = (option: StatusOption | null) => {
    setSelectedStatus(option);
    setStatusSearch(option?.label ?? '');
  };

  const hasStorageLocationUpdate =
    allowStorageLocationUpdate && Boolean(storageLocation?.id);
  const hasStatusUpdate = allowStatusUpdate && selectedStatus !== null;
  const hasUpdates = hasStorageLocationUpdate || hasStatusUpdate;

  const selectedItemLabels = selectedItems
    .map((item, index) => {
      const name = item.name ?? '';
      if (itemType === 'embryo') {
        const embryo = item as ShipmentEmbryo;
        const parts = [
          embryo.cane ? (
            <>
              <strong>Cane:</strong> {embryo.cane}
            </>
          ) : null,
          embryo.strawNumber ? (
            <>
              <strong>Straw:</strong> {embryo.strawNumber}
            </>
          ) : null,
        ].filter(Boolean);
        if (parts.length === 0) return name || null;
        return (
          <span key={index}>
            {name} —{' '}
            {parts.map((part, i) => (
              <span key={i}>
                {i > 0 && ', '}
                {part}
              </span>
            ))}
          </span>
        );
      }
      if (itemType === 'semen') {
        const semen = item as ShipmentSemen;
        if (!semen.colCode) return name || null;
        return (
          <span key={index}>
            {name} — <strong>Col Code:</strong> {semen.colCode}
          </span>
        );
      }
      return name || null;
    })
    .filter(Boolean);

  const resetForm = () => {
    setStorageLocation(null);
    setSelectedSites([]);
    setSelectedStatus(null);
    setStatusSearch('');
  };

  const handleHide = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasUpdates) return;

    await onSubmit({
      ...(hasStorageLocationUpdate
        ? {
            storageLocationId: storageLocation?.id,
            storageLocationName: storageLocation?.name,
            ...(bulkEmbryoSiteEnabled
              ? { siteId: storageLocation?.siteId ?? undefined }
              : {}),
          }
        : {}),
      ...(hasStatusUpdate ? { status: Number(selectedStatus!.value) } : {}),
    });

    resetForm();
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && handleHide()}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              itemType === 'embryo'
                ? singleEmbryoEditEnabled
                  ? 'shipping.bulkEditItems.titleEditEmbryos'
                  : 'shipping.bulkEditItems.titleEmbryos'
                : 'shipping.bulkEditItems.titleSemen',
            )}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="overflow-x-hidden">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-medium">
                {t(
                  selectedItems.length === 1
                    ? 'shipping.bulkEditItems.selectedRecord'
                    : 'shipping.bulkEditItems.selectedRecords',
                  { count: selectedItems.length },
                )}
              </div>
              {selectedItemLabels.length > 0 && (
                <div className="mt-1 max-h-24 overflow-y-auto text-slate-600">
                  {selectedItemLabels.map((label, i) => (
                    <div key={i}>{label}</div>
                  ))}
                </div>
              )}
            </div>

            {allowStorageLocationUpdate && (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t(
                      'shipping.bulkEditItems.filterBySite',
                      'Filter Storage Location by Site',
                    )}{' '}
                    <span className="text-muted-foreground font-normal">
                      ({t('common.optional', 'optional')})
                    </span>
                  </Label>
                  <SitesMultiCombobox
                    value={selectedSites}
                    onChange={(sites) => {
                      setSelectedSites(sites);
                      setStorageLocation(null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t('shipping.bulkEditItems.storageLocation')}{' '}
                    <span className="text-muted-foreground font-normal">
                      ({t('common.optional', 'optional')})
                    </span>
                  </Label>
                  <StorageLocationCombobox
                    value={storageLocation}
                    onChange={setStorageLocation}
                    siteIds={selectedSiteIds}
                  />
                </div>
              </>
            )}

            {allowStatusUpdate && (
              <div className="flex flex-col gap-1.5">
                <Label>
                  {t('shipping.bulkEditItems.status')}{' '}
                  <span className="text-muted-foreground font-normal">
                    ({t('common.optional', 'optional')})
                  </span>
                </Label>
                <Combobox
                  items={statusOptions}
                  value={selectedStatus}
                  onValueChange={handleStatusChange}
                  itemToStringValue={(opt) => String(opt.value)}
                  itemToStringLabel={(opt) => opt.label}
                >
                  <ComboboxInput
                    placeholder={t(
                      'shipping.bulkEditItems.selectStatus',
                      'Select status',
                    )}
                    className="w-full"
                    value={statusSearch}
                    onChange={(e) => {
                      setStatusSearch(e.target.value);
                      setSelectedStatus(null);
                    }}
                    showClear
                  />
                  <ComboboxContent className="min-w-(--anchor-width)">
                    <ComboboxEmpty>
                      {t('common.noResultsFound', 'No results found')}
                    </ComboboxEmpty>
                    <ComboboxList>
                      {(option: StatusOption) => (
                        <ComboboxItem key={option.value} value={option}>
                          {option.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
            )}

            <DialogFooter className="mx-0 mb-0">
              <Button
                variant="outline"
                type="button"
                onClick={handleHide}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={!hasUpdates || isSubmitting}>
                {t('shipping.bulkEditItems.applyToSelected')}
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
